import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ExcelImportSection from '../../components/ExcelImportSection/ExcelImportSection';
import OutputButtonSection from '../../components/OutputButtonSection/OutputButtonSection';
import ExcelReviewTable from '../../components/ExcelReviewTable/ExcelReviewTable';
import * as XLSX from 'xlsx';
import FileListView from '../../components/FileListPage/FileListPage';
import FilePreviewView from '../../components/FilePreviewPage/FilePreviewPage';

import './FileHandler.css';

const MIN_LEFT = 0;
const MIN_RIGHT = 248;
const SIDEBAR = 72;
const DRAG_BAR = 8;

const FileHandler = () => {
  const baseUrl = process.env.REACT_APP_BASE_URL_BE;
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedOutputRow, setSelectedOutputRow] = useState(null);
  const [history, setHistory] = useState([
  ]);
  const [excelPreviewData, setExcelPreviewData] = useState([]);
  const [excelPreviewHeaders, setExcelPreviewHeaders] = useState([]);
  const [excelPreviewMerges, setExcelPreviewMerges] = useState([]);
  const [outputFiles, setOutputFiles] = useState([]);
  const [rating, setRating] = useState(0);
  const [cardOpen, setCardOpen] = useState(true);

  // State cho sidebar
  const [selectedSidebar, setSelectedSidebar] = useState('dashboard');

  // State cho kéo chiều rộng
  const [leftWidth, setLeftWidth] = useState(640);
  const [draggingVertical, setDraggingVertical] = useState(false);

  // Thêm states mới cho drag ngang
  const [splitMode, setSplitMode] = useState('horizontal'); // 'vertical' | 'horizontal'
  const [topHeight, setTopHeight] = useState(300); // chiều cao phần trên
  const [draggingHorizontal, setDraggingHorizontal] = useState(false);

  const [filterName, setFilterName] = useState('');
  const [filterDate, setFilterDate] = useState('');
  const [sortDateOrder, setSortDateOrder] = useState('desc'); // 'asc' | 'desc'
  const [sortRateOrder, setSortRateOrder] = useState(null);   // null | 'asc' | 'desc'

  const [comment, setComment] = useState("");
  const [showCommentBox, setShowCommentBox] = useState(false);

  const [linkedType, setLinkedType] = useState('demand_sheet1');
  const [linkedHeaders, setLinkedHeaders] = useState([]);
  const [linkedData, setLinkedData] = useState([]);
  const [linkedError, setLinkedError] = useState('');

  const [showLinkedPreview, setShowLinkedPreview] = useState(true);

  const handleApprove = (id) => {
    setHistory(h =>
      h.map(file => file.id === id ? { ...file, status: "approved" } : file)
    );
  };

  const handleCancel = (id) => {
    setHistory(h =>
      h.map(file => file.id === id ? { ...file, status: "canceled" } : file)
    );
  };

  const [excelPreviewCellStyles, setExcelPreviewCellStyles] = useState([]); // Thêm state mới

  const handleOutputClick = async (id) => {
    setSelectedOutputRow(id);
    setShowCommentBox(true);
    setComment(""); // reset trước khi fetch

    const file = outputFiles.find(f => f.id === id);

    try {
      if (file && file.name) {
        const bucket = "meisei-output";
        const filename = file.name;
        const filetype = filename.endsWith('.parquet') ? 'parquet' : 'excel';
        const apiUrl = `${baseUrl}/minio/load-file?bucket=${bucket}&filename=${filename}&filetype=${filetype}`;

        const response = await fetch(apiUrl);
        const json = await response.json();

        if (json.error) throw new Error(json.error);

        if (filetype === 'excel') {
          setExcelPreviewHeaders([]); // Không cần headers riêng nữa
          setExcelPreviewData(json.tableData || []);
          setExcelPreviewMerges(
            (json.merges || []).map(m => ({
              s: { r: m.startRow, c: m.startCol },
              e: { r: m.endRow, c: m.endCol }
            }))
          );
          setExcelPreviewCellStyles(json.cellStyles || []);
        } else {
          // parquet giữ nguyên như cũ
          let headerRow = Object.keys(json[0] || {});
          headerRow = headerRow.map(h => /^Unnamed: \d+$/i.test(h) ? '' : h);
          const dataRows = json.map(row => headerRow.map((_, i) => row[Object.keys(row)[i]]));
          const getColName = (idx) => {
            let name = '';
            let n = idx;
            do {
              name = String.fromCharCode(65 + (n % 26)) + name;
              n = Math.floor(n / 26) - 1;
            } while (n >= 0);
            return name;
          };
          const headers = Array.from({ length: headerRow.length }, (_, i) => getColName(i));
          const data = [headerRow, ...dataRows];

          setExcelPreviewHeaders(headers);
          setExcelPreviewData(data);
          setExcelPreviewMerges([]);
          setExcelPreviewCellStyles([]);
        }
      }
    } catch (err) {
      setExcelPreviewHeaders([]);
      setExcelPreviewData([]);
      setExcelPreviewMerges([]);
      setExcelPreviewCellStyles([]);
      console.error('Error loading file:', err);
    }

    try {
      const res = await fetch(`${baseUrl}/postgres/get-comment?id=${id}`);
      const json = await res.json();
      if (json.comment !== undefined && json.comment !== null) {
        setComment(json.comment);
      }
    } catch (err) {
      setComment("");
    }
  };

  const handleRatingChange = async (id, value) => {
    setOutputFiles(files =>
      files.map(f => f.id === id ? { ...f, rating: f.rating === value ? 0 : value } : f)
    );
    try {
      await fetch(`${baseUrl}/postgres/update-rate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id, rate: value === outputFiles.find(f => f.id === id)?.rating ? 0 : value })
      });
    } catch (err) {
      console.error("Error updating rate:", err);
    }
  };

  // Kéo thanh chia đôi
  const handleHorizontalDragStart = (e) => {
    setDraggingHorizontal(true);
    document.body.style.cursor = 'row-resize';
  };

  // Kéo thanh chia đôi
  const handleDragStart = (e) => {
    setDraggingVertical(true);
    document.body.style.cursor = 'col-resize';
  };

  React.useEffect(() => {
    const handleDrag = (e) => {
      if (!draggingVertical) return;
      const total = window.innerWidth - SIDEBAR - DRAG_BAR;
      const maxLeft = total - MIN_RIGHT;
      setLeftWidth(Math.max(MIN_LEFT, Math.min(e.clientX - SIDEBAR, maxLeft)));
    };
    const handleDragEnd = () => {
      setDraggingVertical(false);
      document.body.style.cursor = '';
    };
    if (draggingVertical) {
      window.addEventListener('mousemove', handleDrag);
      window.addEventListener('mouseup', handleDragEnd);
    }
    return () => {
      window.removeEventListener('mousemove', handleDrag);
      window.removeEventListener('mouseup', handleDragEnd);
    };
  }, [draggingVertical]);

  // Effect cho drag ngang
  useEffect(() => {
    if (!draggingHorizontal) return;
    const handleMove = (e) => {
      const container = document.querySelector('.file-handler-layout');
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const min = 120; // chiều cao tối thiểu phần trên
      const max = rect.height - 200; // chừa tối thiểu cho phần dưới
      const newHeight = Math.max(min, Math.min(e.clientY - rect.top, max));
      setTopHeight(newHeight);
    };
    const handleUp = () => {
      setDraggingHorizontal(false);
      document.body.style.cursor = '';
    };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, [draggingHorizontal]);

  const fetchFiles = async () => {
    try {
      const res = await fetch(`${baseUrl}/postgres/list-files`);
      const json = await res.json();
      if (json.files) {
        setOutputFiles(
          json.files.map(f => ({
            id: f.id,
            name: f.filename,
            generatedAt: f.date || "-",
            size: "-",
            rating: f.rate || 0
          }))
        );
      }
    } catch (err) {
      setOutputFiles([]);
      console.error("Error fetching file list:", err);
    }
  };

  useEffect(() => {
    fetchFiles();
  }, []);

  useEffect(() => {
    if (
      outputFiles.length > 0 &&
      (selectedOutputRow === null || !outputFiles.some(f => f.id === selectedOutputRow))
    ) {
      // Tự động chọn và load file đầu tiên
      handleOutputClick(outputFiles[0].id);
    }
    // eslint-disable-next-line
  }, [outputFiles]);

  // Lọc và sắp xếp file theo tên, ngày, rate
  const filteredFiles = outputFiles
    .filter(file => {
      const matchName = file.name.toLowerCase().includes(filterName.toLowerCase());
      const matchDate = filterDate ? (file.generatedAt || "-").includes(filterDate) : true;
      return matchName && matchDate;
    })
    .sort((a, b) => {

      if (sortRateOrder) {
        if (sortRateOrder === 'asc') return (a.rating || 0) - (b.rating || 0);
        else return (b.rating || 0) - (a.rating || 0);
      }
      if (!a.generatedAt || !b.generatedAt) return 0;
      if (sortDateOrder === 'asc') {
        return a.generatedAt.localeCompare(b.generatedAt);
      } else {
        return b.generatedAt.localeCompare(a.generatedAt);
      }
    });

  // Thêm hàm tải file
  const handleDownloadFile = async () => {
    if (!selectedOutputRow) return;
    const file = outputFiles.find(f => f.id === selectedOutputRow);
    if (!file) return;
    try {
      const url = `${baseUrl}/postgres/download-file?filename=${encodeURIComponent(file.name)}`;
      window.open(url, '_blank');
    } catch (err) {
      alert('ファイルのダウンロードに失敗しました！');
    }
  };

  // Thêm hàm xoá file
  const handleDeleteFile = async () => {
    if (!selectedOutputRow) return;
    const file = outputFiles.find(f => f.id === selectedOutputRow);
    if (!file) return;
    if (!window.confirm(`本当にこのファイルを削除しますか "${file.name}"?`)) return;
    try {
      const res = await fetch(`${baseUrl}/postgres/delete-file`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: file.id })
      });
      const json = await res.json();
      if (json.error) throw new Error(json.error);
      // Sau khi xoá, reload lại danh sách file
      fetchFiles();
      setSelectedOutputRow(null);
      setExcelPreviewHeaders([]);
      setExcelPreviewData([]);
      setExcelPreviewMerges([]);
      setShowCommentBox(false);
      setComment("");
      alert('ファイルを正常に削除しました！');
      handleClosePreviewFile(selectedOutputRow);
      handleBackToList(); // Quay về menu
    } catch (err) {
      alert('ファイルの削除に失敗しました！');
    }
  };

  function reorderHeadersAndData(headers, data) {
    if (!headers || headers.length === 0) return { headers, data };
    let indexCol = headers.find(h =>
      String(h).toLowerCase() === 'index' ||
      String(h).toLowerCase() === 'no' ||
      String(h).includes('番号')
    );
    let indexIdx = indexCol ? headers.indexOf(indexCol) : 0;

    const isPureNumber = h => typeof h === 'number' || (typeof h === 'string' && /^\d+$/.test(h.trim()));
    const isTextOrUnnamed = h =>
      !isPureNumber(h) || String(h).toLowerCase().includes('unnamed') || String(h).toLowerCase().includes('noname');

    let textCols = headers
      .map((h, i) => ({ h, i }))
      .filter(({ h, i }) => i !== indexIdx && isTextOrUnnamed(h));
    let numCols = headers
      .map((h, i) => ({ h, i }))
      .filter(({ h, i }) => i !== indexIdx && !isTextOrUnnamed(h));

    // Đặt lại tên cột nếu là Unnamed/Noname/rỗng thành A, B, C,...
    const getColName = (idx) => {
      let name = '';
      let n = idx;
      do {
        name = String.fromCharCode(65 + (n % 26)) + name;
        n = Math.floor(n / 26) - 1;
      } while (n >= 0);
      return name;
    };
    let newHeaders = headers.map((h, i) =>
      !h || String(h).toLowerCase().includes('unnamed') || String(h).toLowerCase().includes('noname')
        ? getColName(i)
        : h
    );

    let newOrder = [
      indexIdx,
      ...textCols.map(({ i }) => i),
      ...numCols.map(({ i }) => i)
    ];

    let finalHeaders = newOrder.map(i => newHeaders[i]);
    let newData = data.map(row => newOrder.map(i => row[i]));
    return { headers: finalHeaders, data: newData };
  }

  const [linkedPanelWidth, setLinkedPanelWidth] = useState(420);
  const [draggingLinked, setDraggingLinked] = useState(false);

  useEffect(() => {
    if (!draggingLinked) return;
    const handleMove = (e) => {
      // Giới hạn min/max cho linked panel
      const min = 220, max = 700;
      const rect = document.getElementById('excel-preview-link-flex')?.getBoundingClientRect();
      if (rect) {
        const newWidth = Math.max(min, Math.min(rect.right - e.clientX, max));
        setLinkedPanelWidth(newWidth);
      }
    };
    const handleUp = () => setDraggingLinked(false);
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, [draggingLinked]);

  useEffect(() => {
    if (!selectedOutputRow || !linkedType) {
      setLinkedHeaders([]);
      setLinkedData([]);
      return;
    }
    setLinkedError('');
    setLinkedHeaders([]);
    setLinkedData([]);
    (async () => {
      try {
        const res = await fetch(
          `${baseUrl}/minio/load-linked-file?id=${selectedOutputRow}&type=${linkedType}&filetype=parquet`
        );
        const json = await res.json();
        if (json.error) throw new Error(json.error);
        if (Array.isArray(json) && json.length > 0) {
          // Lấy header thực sự từ response (Object.keys)
          const headerRow = Object.keys(json[0] || {});
          // Data cũ
          const dataRows = json.map(row => headerRow.map(h => row[h]));
          // Header hiển thị là A, B, C,...
          const getColName = (idx) => {
            let name = '';
            let n = idx;
            do {
              name = String.fromCharCode(65 + (n % 26)) + name;
              n = Math.floor(n / 26) - 1;
            } while (n >= 0);
            return name;
          };
          const headers = Array.from({ length: headerRow.length }, (_, i) => getColName(i));
          // Gộp header thực sự vào dòng đầu data
          const data = [headerRow, ...dataRows];
          setLinkedHeaders(headers);
          setLinkedData(data);
        } else {
          setLinkedHeaders([]);
          setLinkedData([]);
        }
      } catch (err) {
        setLinkedError('関連ファイルを読み込めませんでした');
      }
    })();
  }, [selectedOutputRow, linkedType, baseUrl]);

  // Thay vì sidebarItems tĩnh, render động các folder theo các file đang preview
  // Giả sử bạn có mảng previewFileIds chứa các file đang được preview (có thể là 1 hoặc nhiều)
  const [previewFileIds, setPreviewFileIds] = useState([]); // array of file ids

  // Khi vào preview 1 file:
  // (Removed duplicate handleRowClick)

  // Khi đóng preview 1 file:
  const handleClosePreviewFile = (fileId) => {
    setPreviewFileIds(ids => ids.filter(id => id !== fileId));
    if (previewFileId === fileId) {
      setViewMode('list');
      setPreviewFileId(null);
      setSelectedOutputRow(null);
      setExcelPreviewHeaders([]);
      setExcelPreviewData([]);
      setExcelPreviewMerges([]);
      setShowCommentBox(false);
      setComment("");
    }
  };

  useEffect(() => {
    if (leftWidth < 428 && cardOpen) {
      setCardOpen(false);
    }
  }, [leftWidth, cardOpen]);

  const [isComposing, setIsComposing] = useState(false);

  const handleSubmitComment = async () => {
    try {
      await fetch(`${baseUrl}/postgres/update-comment`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: selectedOutputRow, comment }),
      });
      setShowCommentBox(false);
    } catch (err) {
      alert("コメント送信に失敗しました");
    }
  };

  const [viewMode, setViewMode] = useState('list'); // 'list' | 'preview'
  const [previewFileId, setPreviewFileId] = useState(null);

  const handleRowClick = (fileId) => {
    setPreviewFileId(fileId);
    setViewMode('preview');
    setSelectedSidebar(fileId);
    setPreviewFileIds(ids => ids.includes(fileId) ? ids : [...ids, fileId]);
    handleOutputClick(fileId);

    const params = new URLSearchParams(location.search);
    params.set('file_id', fileId);
    if (filterName) params.set('filter_name', filterName); else params.delete('filter_name');
    if (filterDate) params.set('filter_date', filterDate); else params.delete('filter_date');
    const newUrl = `/file-handler?${params.toString()}`;
    window.history.replaceState({}, '', newUrl);
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);

    if (filterName) params.set('filter_name', filterName); else params.delete('filter_name');
    if (filterDate) params.set('filter_date', filterDate); else params.delete('filter_date');
    if (previewFileId) {
      params.set('file_id', previewFileId);
    }

    const newUrl = `/file-handler?${params.toString()}`;
    if (newUrl !== window.location.pathname + window.location.search) {
      window.history.replaceState({}, '', newUrl);
    }
  }, [filterName, filterDate, previewFileId, location.search]);

  const handleBackToList = () => {
    setViewMode('list');
    setPreviewFileId(null);
    setSelectedOutputRow(null);
    setExcelPreviewHeaders([]);
    setExcelPreviewData([]);
    setExcelPreviewMerges([]);
    setShowCommentBox(false);
    setComment("");
    setSelectedSidebar('dashboard');

    const params = new URLSearchParams(location.search);
    params.delete('file_id');
    const newUrl = `/file-handler?${params.toString()}`;
    window.history.replaceState({}, '', newUrl);
  };

  const handleSettingsClick = () => {
    setSelectedSidebar('settings');
    setViewMode('list');
  };


  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [commentLoading, setCommentLoading] = useState(false);
  const [commentType, setCommentType] = useState('quality'); // 'quality' | 'order'
  const [commentUser, setCommentUser] = useState('');
  const commentInputRef = useRef(null);

  useEffect(() => {
    if (!previewFileId) {
      setComments([]);
      return;
    }
    const fetchComments = async () => {
      try {
        const res = await fetch(`${baseUrl}/mongo/get-comments?file_id=${previewFileId}`);
        const json = await res.json();
        setComments(json.comments || []);
      } catch (err) {
        setComments([]);
      }
    };
    fetchComments();
  }, [previewFileId, baseUrl]);

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    setCommentLoading(true);
    try {
      await fetch(`${baseUrl}/mongo/add-comment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          file_id: previewFileId,
          user: commentUser || "guest",
          type: commentType,
          content: newComment.trim(),
        }),
      });
      setNewComment('');
      // Reload comment list
      const res = await fetch(`${baseUrl}/mongo/get-comments?file_id=${previewFileId}`);
      const json = await res.json();
      setComments(json.comments || []);
      setTimeout(() => commentInputRef.current?.focus(), 100);
    } catch (err) {
      alert('コメントの送信に失敗しました。');
    }
    setCommentLoading(false);
  };

  const handleDeleteAllComments = async () => {
    if (!previewFileId) return;
    if (!window.confirm("すべてのコメントを削除してもよろしいですか?")) return;
    setCommentLoading(true);
    try {
      await fetch(`${baseUrl}/mongo/delete-all-comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ file_id: previewFileId }),
      });
      setComments([]);
    } catch (err) {
      alert('Xoá comment thất bại!');
    }
    setCommentLoading(false);
  };

  const handleDeleteComment = async (index) => {
    if (!previewFileId) return;
    if (!window.confirm("このコメントを削除してもよろしいですか?")) return;
    setCommentLoading(true);
    try {
      await fetch(`${baseUrl}/mongo/delete-comment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ file_id: previewFileId, index }),
      });
      // Reload comment list
      const res = await fetch(`${baseUrl}/mongo/get-comments?file_id=${previewFileId}`);
      const json = await res.json();
      setComments(json.comments || []);
    } catch (err) {
      alert('Xoá comment thất bại!');
    }
    setCommentLoading(false);
  };

  function getExcelColName(idx) {
    let name = '';
    let n = idx;
    while (n >= 0) {
      name = String.fromCharCode((n % 26) + 65) + name;
      n = Math.floor(n / 26) - 1;
    }
    return name;
  }

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const name = params.get('filter_name') || '';
    const date = params.get('filter_date') || '';
    const fileId = params.get('file_id');

    if (filterName !== name) setFilterName(name);
    if (filterDate !== date) setFilterDate(date);

    if (
      fileId &&
      outputFiles.some(f => f.id === Number(fileId)) &&
      previewFileId !== fileId
    ) {
      console.log('==> Call handleRowClick', fileId);
      handleRowClick(fileId);
    }
  }, [location.search, outputFiles]);

  return (
    <div style={{ display: 'flex', height: '100vh', width: '100vw'}}>
      {/* Sidebar giữ nguyên */}
      <div className="file-handler-sidebar" style={{
        width: 56,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        paddingTop: 12,
        borderRight: '1px solid #e3e8ef',
        boxShadow: '0 2px 8px rgba(66,133,244,0.08)',
        background: 'rgb(248, 250, 252)',
        zIndex: 1
      }}>
        {/* Dashboard icon */}
        <div
          className={`sidebar-item${selectedSidebar === 'dashboard' ? ' active' : ''}`}
          style={{
            width: 48,
            height: 36,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 8,
            borderRadius: 12,
            cursor: 'pointer',
            background: selectedSidebar === 'dashboard' ? '#2563eb' : 'none',
            boxShadow: selectedSidebar === 'dashboard' ? '0 2px 8px rgba(37,99,235,0.12)' : 'none',
            transition: 'background 0.2s, box-shadow 0.2s',
            fontSize: 24
          }}
          onClick={() => {
            setSelectedSidebar('dashboard');
            setViewMode('list');
            // KHÔNG reset previewFileIds ở đây!
            // KHÔNG reset setPreviewFileId(null) ở đây!
            // KHÔNG reset các state liên quan folder ở đây!
            // Chỉ chuyển về màn hình list, giữ nguyên các folder đang mở trên sidebar
            const params = new URLSearchParams(location.search);
            params.delete('file_id');
            const newUrl = `/file-handler?${params.toString()}`;
            window.history.replaceState({}, '', newUrl);
          }}
        >
          <span className="material-symbols-rounded">menu</span>
        </div>

        {previewFileIds.map(fileId => {
          const file = outputFiles.find(f => f.id === fileId);
          return (
            <div
              key={fileId}
              className={`sidebar-item${selectedSidebar === fileId ? ' active' : ''}`}
              style={{
                width: 48,
                height: 36,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 8,
                borderRadius: 12,
                cursor: 'pointer',
                background: selectedSidebar === fileId ? '#2563eb' : 'none',
                boxShadow: selectedSidebar === fileId ? '0 2px 8px rgba(37,99,235,0.12)' : 'none',
                transition: 'background 0.2s, box-shadow 0.2s',
                fontSize: 24
              }}
              title={file?.name || 'File'}
              onClick={() => {
                setSelectedSidebar(fileId);
                setViewMode('preview');
                setPreviewFileId(fileId);
                handleOutputClick(fileId);
              }}
            >
              <span className="material-symbols-rounded">folder</span>
            </div>
          );
        })}
        {/* Settings icon */}
        <div
          className={`sidebar-item${selectedSidebar === 'settings' ? ' active' : ''}`}
          style={{
            width: 48,
            height: 36,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 8,
            borderRadius: 12,
            cursor: 'pointer',
            background: selectedSidebar === 'settings' ? '#2563eb' : 'none',
            boxShadow: selectedSidebar === 'settings' ? '0 2px 8px rgba(37,99,235,0.12)' : 'none',
            transition: 'background 0.2s, box-shadow 0.2s',
            fontSize: 24
          }}
          onClick={handleSettingsClick}
        >
          <span className="material-symbols-rounded">settings</span>
        </div>
      </div>
      {/* Main content */}
      <div style={{ display: 'flex', flex: 1, height: '100%' , overflow: 'hidden'}} className="file-handler-layout">
        {viewMode === 'list' && (
          <div style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column', // Luôn luôn là column cho list mode
          }}>
            {/* Phần trên - Card và controls */}
            <div
              className="file-handler-page"
              style={{
                width: '100%',
                height: `${topHeight}px`,
                minHeight: 80,
                padding: 4,
                display: 'flex',
                flexDirection: 'column',
                overflowY: 'auto'
              }}
            >
              {/* Card section giữ nguyên */}
              <div
                className="file-handler-card fancy-card"
                style={{
                  display: viewMode === 'preview' ? 'none' : undefined
                }}
              >
                <div className="fancy-card__header">
                  <div className="fancy-card__title-wrap">
                    <span className="material-symbols-rounded fancy-card__title-icon">inventory_2</span>
                    <h1 className="fancy-card__title">ファイル管理</h1>
                    <span className="fancy-card__badge">{filteredFiles.length} files</span>
                  </div>
                  <div className="fancy-card__actions">
                    <button
                      className="icon-btn ghost"
                      title={cardOpen ? 'Ẩn khu vực thao tác' : 'Hiện khu vực thao tác'}
                      onClick={() => {
                        if (!cardOpen && leftWidth < 428) setLeftWidth(430);
                        setCardOpen(o => !o);
                      }}
                    >
                      <span className="material-symbols-rounded">
                        {cardOpen ? 'expand_less' : 'expand_more'}
                      </span>
                    </button>
                  </div>
                </div>

                <div className={`fancy-card__body ${cardOpen ? 'open' : 'closed'}`}>
                  <div className="toolbar-modern-grid">
                    <div className="toolbar-block">
                      <div className="toolbar-block__head">
                        <span className="material-symbols-rounded block-icon upload">file_upload</span>
                        <span className="block-title">Import</span>
                      </div>
                      <div className="toolbar-block__body">
                        <ExcelImportSection />
                      </div>
                    </div>

                    <div className="toolbar-block">
                      <div className="toolbar-block__head">
                        <span className="material-symbols-rounded block-icon gen">play_circle</span>
                        <span className="block-title">Output</span>
                      </div>
                      <div className="toolbar-block__body">
                        <OutputButtonSection onReload={fetchFiles} />
                      </div>
                    </div>

                    
                  </div>
                  
                </div>
                <div className="toolbar-modern-grid">
                    <div className="toolbar-block slim-info">
                        <div className="meta-line">
                          <span className="material-symbols-rounded meta-icon">info</span>
                          <span className="meta-text">右側の表でファイルを選択するとプレビューが表示されます。</span>
                        </div>
                    </div>
                  </div>
              </div>   
            </div>
            {/* Drag bar ngang (kéo lên xuống) */}
            <div
              className="drag-bar-horizontal"
              style={{
                width: '100%',
                height: 6,
                cursor: 'row-resize',
                background: 'linear-gradient(to right, #e3e8ef, #d5dae1, #e3e8ef)',
                userSelect: 'none',
                zIndex: 2,
                boxShadow: 'inset 0 0 2px rgba(0,0,0,0.08)'
              }}
              onMouseDown={handleHorizontalDragStart}
            />

            {/* Phần dưới - Table */}
            <div
              className="file-handler-history-container"
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                overflow: 'auto',
                marginTop: 0,
              }}
            >
              <div className="file-handler-history" style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, padding: 4}}>
                {/* Filter và table giữ nguyên như cũ */}
                <div className="file-handler-history-filter">
                  <div style={{ display: 'flex', gap: 4, padding: 0}}>
                    <input
                      type="text"
                      placeholder="ファイル名で検索"
                      value={filterName}
                      onChange={e => setFilterName(e.target.value)}
                      style={{ padding: 4, borderRadius: 4, border: '1px solid #ccc', minWidth: 120, height: 32 }}
                    />
                    <input
                      type="text"
                      placeholder="日付で検索 (例: 2024-07-29)"
                      value={filterDate}
                      onChange={e => setFilterDate(e.target.value)}
                      style={{ padding: 4, borderRadius: 4, border: '1px solid #ccc', minWidth: 120, height: 32  }}
                    />
                  </div>
                </div>
                <div className="file-handler-history-table" style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'auto' }}>
                  {/* Table content giữ nguyên */}
                  <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                    <thead>
                      <tr>
                        <th style={{ border: '1px solid #ccc' }}>ファイル名</th>
                        <th style={{ border: '1px solid #ccc' }}>アップロード日時</th>
                        <th style={{ border: '1px solid #ccc' }}>評価</th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* Table rows giữ nguyên như code cũ */}
                      {filteredFiles.map(file => (
                        <tr
                          key={file.id}
                          className={selectedOutputRow === file.id ? "selected" : ""}
                          onClick={() => handleRowClick(file.id)}
                          style={{ cursor: "pointer" }}
                        >
                          <td>{file.name}</td>
                          <td>
                            {file.generatedAt
                              ? new Date(file.generatedAt + 'Z').toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' })
                              : "-"}
                          </td>
                          <td>
                            <span className="rating-stars">
                              {[1, 2, 3, 4, 5].map(i => (
                                <span
                                  key={i}
                                  className={i <= file.rating ? "active" : "inactive"}
                                  onClick={e => {
                                    e.stopPropagation();
                                    handleRatingChange(file.id, file.rating === i ? 0 : i);
                                  }}
                                  role="img"
                                  aria-label={`${i} star`}
                                >★</span>
                              ))}
                            </span>
                            {file.rating > 0 && (
                              <span style={{ marginLeft: 6, color: "#2563eb", fontWeight: 500 }}>
                                {file.rating} / 5
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}

                      {history.map(file => (
                        <tr
                          key={file.id}
                          className={selectedRow === file.id ? "selected" : ""}
                          onClick={() => file.status === "pending" && setSelectedRow(file.id)}
                          style={{
                            backgroundColor: file.status === "pending" && selectedRow === file.id ? "#fffbe7" : undefined,
                            cursor: file.status === "pending" ? "pointer" : "default"
                          }}
                        >
                          <td>{file.name}</td>
                          <td>
                            {file.uploadedAt
                              ? new Date(file.uploadedAt + 'Z').toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' })
                              : "-"}
                          </td>
                          <td><span style={{ color: "#888" }}>-</span></td>
                        </tr>
                      ))}

                      {history.length === 0 && filteredFiles.length === 0 && (
                        <tr>
                          <td colSpan={3} style={{ textAlign: "center", color: "#aaa", padding: "12px" }}>
                            履歴がありません
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}
        {viewMode === 'preview' && (
          <div className="file-handler-preview" style={{
            flex: 1,
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto',
            background: 'rgb(219 219 219)',
            boxShadow: '0 0 0.5px #3e4247ff'
          }}>
            <div style={{ boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)', background: '#ffffff', display: 'flex', alignItems: 'center', gap: 8, margin: 4, height: 36, justifyContent: 'space-between' }}>

              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {/* Nút tải file */}
                <button
                  onClick={handleDownloadFile}
                  style={{
                    padding: 6,
                    borderRadius: '50%',
                    border: 'none',
                    color: '#2563eb',
                    fontWeight: 500,
                    cursor: 'pointer',
                    fontSize: 20,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  disabled={!selectedOutputRow}
                  title="ファイルをダウンロード"
                >
                  <span className="material-symbols-rounded">download</span>
                </button>
                {/* Nút xoá file */}
                <button
                  onClick={handleDeleteFile}
                  style={{
                    padding: 6,
                    borderRadius: '50%',
                    border: 'none',
                    color: '#e11d48',
                    fontWeight: 500,
                    cursor: 'pointer',
                    fontSize: 20,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  disabled={!selectedOutputRow}
                  title="ファイルを削除"
                >
                  <span className="material-symbols-rounded">delete</span>
                </button>
                {/* Chọn loại linked file */}
                <span style={{ fontSize: 12, marginLeft: 18, color: '#525252ff', fontWeight: 500 }}>関連ファイルを表示:</span>
                <select
                  value={linkedType}
                  onChange={e => setLinkedType(e.target.value)}
                  className="custom-select"
                  style={{ minWidth: 160, padding: 4, borderRadius: 2, border: '1px solid #b1b1b1ff' }}
                >
                  <option value="demand_sheet1">予約情報 Sheet 1</option>
                  <option value="demand_sheet2">予約情報 Sheet 2</option>
                  <option value="available">在庫情報</option>
                </select>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {/* Nút quay lại */}
                <button
                  onClick={handleBackToList}
                  style={{
                    padding: 6,
                    borderRadius: '50%',
                    border: 'none',
                    color: '#2563eb',
                    fontWeight: 500,
                    cursor: 'pointer',
                    fontSize: 20,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  title="一覧に戻る"
                >
                  <span className="material-symbols-rounded">arrow_back</span>
                </button>
                {/* Nút đóng preview file hiện tại */}
                <button
                  onClick={() => {
                    if (previewFileId) handleClosePreviewFile(previewFileId);
                  }}
                  style={{
                    padding: 6,
                    borderRadius: '50%',
                    border: 'none',
                    color: '#e11d48',
                    fontWeight: 500,
                    cursor: 'pointer',
                    fontSize: 20,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  title="ファイルを閉じる"
                  disabled={!previewFileId}
                >
                  <span className="material-symbols-rounded">close</span>
                </button>
              </div>
            </div>
            {/* Bảng preview giữ nguyên */}
            {selectedOutputRow && (
              <div
                id="excel-preview-link-flex"
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  height : '100%',
                  overflow: 'hidden',
                  minHeight: 320,
                  position: 'relative',
                  marginBottom: '4px',
                  marginRight: '4px',
                  marginLeft: '4px',
                }}
              >
                {/* Excel preview (bên trái) */}
                <div
                  style={{
                    flex: 1,
                    minWidth: 120,
                    display: 'flex',
                    flexDirection: 'row',
                    height: '100%',
                    minHeight: 0,
                    overflow: 'hidden',
                    gap: '4px',
                  }}
                >
                  <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', padding: 0  }}>
                    <ExcelReviewTable
                      filename={outputFiles.find(f => f.id === selectedOutputRow)?.name}
                      headers={
                        excelPreviewData[0]
                          ? Array.from({ length: excelPreviewData[0].length }, (_, i) => getExcelColName(i))
                          : []
                      }
                      data={excelPreviewData}
                      merges={excelPreviewMerges}
                      cellStyles={excelPreviewCellStyles}
                    />
                  </div>
                  {viewMode === 'preview' && (
                      <div
                        style={{
                          width: 320,
                          minWidth: 220,
                          maxWidth: 620,
                          border: '1px solid #e3e8ef',
                          background: '#fff',
                          display: 'flex',
                          flexDirection: 'column',
                          height: '100%',
                          padding: 4
                        }}
                      >
                        <div style={{ padding: 6, borderBottom: '1px solid #e3e8ef', fontWeight: 500, color: '#585858ff', fontSize: 12, marginBottom: 2 }}>
                          コメント
                        </div>
                        <div style={{ flex: 1, overflowY: 'auto', padding: 12, display: 'flex', flexDirection: 'column', gap: 10, background: '#f9fafb' }}>
                          {comments.length === 0 && (
                            <div style={{ color: '#aaa', textAlign: 'center', marginTop: 24 }}>コメントがありません</div>
                          )}
                          {[...comments].reverse().map((c, idx) => (
                            <div key={comments.length - 1 - idx} style={{ background: '#fefefe', borderRadius: 8, padding: 8, fontSize: 13, border: '1px solid #e3e8ef' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                                <span style={{ fontWeight: 500, color: '#2563eb' }}>{c.user || 'ゲスト'}</span>
                                <span style={{
                                  fontSize: 12,
                                  color: c.type === 'other' ? '#eab308' : '#2563eb',
                                  background: c.type === 'other' ? '#fef9c3' : '#e0e7ff',
                                  borderRadius: 4,
                                  padding: '1px 6px',
                                  marginLeft: 4,
                                  fontWeight: 500,
                                }}>
                                  {c.type === 'other' ? 'その他' : '精度関連'}
                                </span>
                                {/* Nút xoá comment */}
                                <button
                                  style={{
                                    marginLeft: 'auto',
                                    background: 'none',
                                    border: 'none',
                                    color: '#e11d48',
                                    fontWeight: 500,
                                    cursor: 'pointer',
                                    fontSize: 16,
                                  }}
                                  title="コメントを削除"
                                  onClick={() => handleDeleteComment(comments.length - 1 - idx)}
                                  disabled={commentLoading}
                                >
                                  <span className="material-symbols-rounded">delete</span>
                                </button>
                              </div>
                              <div style={{ whiteSpace: 'pre-line', wordBreak: 'break-word' }}>
                                {c.content}
                              </div>
                              <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                                {c.created_at ? new Date(c.created_at).toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' }) : ''}
                              </div>
                            </div>
                          ))}
                        </div>
                        <div style={{ borderTop: '1px solid #e3e8ef', padding: 4, background: '#fff' }}>
                          <div style={{ display: 'flex', gap: 4, marginBottom: 4, justifyContent: 'space-between', alignItems: 'center' }}>
                            <select
                              value={commentType}
                              onChange={e => setCommentType(e.target.value)}
                              style={{
                                borderRadius: 4,
                                border: '1px solid #2563eb',
                                padding: '4px 8px',
                                fontSize: 13,
                                color: '#2563eb',
                                background: '#ffffffff !important',
                                fontWeight: 500,
                                height: 28,
                              }}
                            >
                              <option value="quality">精度関連</option>
                              <option value="other">その他</option>
                            </select>
                            <input
                              type="text"
                              value={commentUser}
                              onChange={e => setCommentUser(e.target.value)}
                              placeholder="名前"
                              style={{
                                flex: 1,
                                borderRadius: 4,
                                border: '1px solid #ccc',
                                padding: '4px 8px',
                                fontSize: 13,
                              }}
                            />
                          </div>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              background: '#ffffffff',
                              border: '1px solid #e3e8ef',
                              borderRadius: 6,
                              padding: 4,
                              marginBottom: 2,
                              position: 'relative',
                            }}
                          >
                            <textarea
                              ref={commentInputRef}
                              value={newComment}
                              onChange={e => setNewComment(e.target.value)}
                              rows={2}
                              placeholder="コメントを入力してください..."
                              style={{
                                flex: 1,
                                border: 'none',
                                borderRight: '1px solid #e3e8ef',
                                outline: 'none',
                                background: 'transparent',
                                fontSize: 13,
                                resize: 'none',
                                padding: '8px 40px 8px 8px',
                                borderRadius: 0,
                                minHeight: 48,
                                height: 48,
                                maxHeight: 80,
                                color: '#222',
                              }}
                              onCompositionStart={() => setIsComposing(true)}
                              onCompositionEnd={() => setIsComposing(false)}
                              onKeyDown={e => {
                                if (e.key === 'Enter' && !e.shiftKey && !isComposing) {
                                  e.preventDefault();
                                  handleAddComment();
                                }
                              }}
                              disabled={commentLoading}
                            />
                            <button
                              onClick={handleAddComment}
                              disabled={commentLoading || !newComment.trim()}
                              style={{
                                position: 'absolute',
                                right: 10,
                                background: 'transparent',
                                color: '#000000',
                                border: 'none',
                                borderRadius: '50%',
                                width: 32,
                                height: 32,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: 20,
                                cursor: commentLoading ? 'not-allowed' : 'pointer',
                                opacity: commentLoading || !newComment.trim() ? 0.7 : 1,
                                transition: 'background 0.2s',
                              }}
                              title="送信"
                            >
                              <span className="material-symbols-rounded">send</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                </div>
                {/* Drag bar giữa hai bảng */}
                {linkedHeaders.length > 0 && (
                  <div
                    style={{
                      width: 4,
                      cursor: 'col-resize',
                      background: '#e3e8ef',
                      zIndex: 10,
                      userSelect: 'none',
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                    onMouseDown={() => setDraggingLinked(true)}
                  >
                  
                  </div>
                )}
                {/* Linked preview (bên phải) */}
                {linkedHeaders.length > 0 && (() => {
                  const { headers: linkedHeadersSorted, data: linkedDataSorted } = reorderHeadersAndData(
                    linkedHeaders,
                    linkedData
                  );
                  if (!showLinkedPreview) {
                    // Nút mở lại panel khi đang đóng
                    return (
                      <div
                        style={{
                          width: 18,
                          minWidth: 18,
                          maxWidth: 18,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          height: '100%',
                        }}
                      >
                        <button
                          style={{
                            background: 'transparent',
                            color: '#2563eb',
                            border: 'none',
                            borderRadius: '50%',
                            width: 28,
                            height: 28,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 18,
                            fontWeight: 600,
                            gap: 4,
                          }}
                          title="プレビュー"
                          onClick={() => setShowLinkedPreview(true)}
                        >
                          <span className="material-symbols-rounded">chevron_left</span>
                        </button>
                      </div>
                    );
                  }
                  // Panel linked đang mở
                  return (
                    <div
                      style={{
                        width: linkedPanelWidth,
                        minWidth: 120,
                        maxWidth: 700,
                        overflow: 'hidden',
                        borderRadius: 0,
                        marginLeft: 0,
                        padding: 0,
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      {(() => {
                        // const file = outputFiles.find(f => f.id === selectedOutputRow);

                        return (
                          <>
                            {/* Nút tải Demand nếu đang xem demand_sheet1 hoặc demand_sheet2 */}
                            {(linkedType === 'demand_sheet1' || linkedType === 'demand_sheet2') && (
                              <button
                                style={{
                                  position: 'absolute',
                                  top: 8,
                                  right: 32,
                                  zIndex: 20,
                                  background: 'transparent',
                                  border: 'none',
                                  color: '#0d44e8ff',
                                  fontWeight: 500,
                                  cursor: 'pointer',
                                  fontSize: 18,
                                  padding: 4,
                                  borderRadius: '50%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  marginRight: 4,
                                }}
                                title="Download Demand"
                                onClick={() => {
                                  if (selectedOutputRow) {
                                    window.open(
                                      `${baseUrl}/minio/download-demand-file?output_id=${selectedOutputRow}`,
                                      '_blank'
                                    );
                                  }
                                }}
                              >
                                <span className="material-symbols-rounded">download</span>
                              </button>
                            )}
                            {/* Nút tải Available nếu đang xem available */}
                            {linkedType === 'available' && (
                              <button
                                style={{
                                  position: 'absolute',
                                  top: 8,
                                  right: 32,
                                  zIndex: 20,
                                  background: 'transparent',
                                  border: 'none',
                                  color: '#0d44e8ff',
                                  fontWeight: 500,
                                  cursor: 'pointer',
                                  fontSize: 18,
                                  padding: 4,
                                  borderRadius: '50%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                                title="Download Available"
                                onClick={() => {
                                  if (selectedOutputRow) {
                                    window.open(
                                      `${baseUrl}/minio/download-available-file?output_id=${selectedOutputRow}`,
                                      '_blank'
                                    );
                                  }
                                }}
                              >
                                <span className="material-symbols-rounded">download</span>
                              </button>
                            )}
                            {/* Nút đóng preview linked */}
                            <button
                              style={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                zIndex: 20,
                                background: 'transparent',
                                border: 'none',
                                color: '#e11d48',
                                fontWeight: 500,
                                cursor: 'pointer',
                                fontSize: 18,
                                padding: 4,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                              onClick={() => setShowLinkedPreview(false)}
                              title="Đóng"
                            >
                              <span className="material-symbols-rounded">close</span>
                            </button>
                            {/* Excel preview table */}
                            <div style={{ height: '100%', width: '100%', padding: 0, overflow: 'hidden' }}>
                              <ExcelReviewTable
                                filename={`${linkedType}`}
                                headers={linkedHeadersSorted}
                                data={linkedDataSorted}
                                merges={[]}
                              />
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  );
                })()}
              </div>
            )}

          </div>
        )}
      </div>
    </div>
  );
};

export default FileHandler;